import React, { FC, useEffect, useState } from 'react';
import styles from './PowerBIEmbedded.module.scss';
import { Embed, models, service } from 'powerbi-client';
import { PowerBIEmbed } from 'powerbi-client-react';
import Spinner from 'components/atoms/Custom/Spinner/Spinner';
import { BooleanCallback } from 'types/Callbacks';
import { useClassNameBuilderFactory } from 'common/providers/ReactKitProvider';

interface IPowerBIEmbeddedProps {
	reportClass?: string;
	embedUrl: string;
	accessToken: string;
	loading: boolean;
	onRenderedPowerBI: BooleanCallback;
	layoutType?: models.LayoutType;
}

const PowerBIEmbedded: FC<IPowerBIEmbeddedProps> = ({
	reportClass,
	embedUrl,
	accessToken,
	loading,
	onRenderedPowerBI,
	layoutType = models.LayoutType.Master,
}) => {
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [currentReport, setCurrentReport] = useState<Embed | null>(null);
	
	const [embeddedReportConfig, setEmbeddedReportConfig] = useState<models.IReportEmbedConfiguration>({
		type: 'report',
		embedUrl: undefined,
		tokenType: models.TokenType.Embed,
		accessToken: undefined,
		settings: {
			layoutType: layoutType,
			customLayout: {
				displayOption: models.DisplayOption.FitToWidth,
				reportAlignment: models.ReportAlignment.Center,
			},
			navContentPaneEnabled: false,
		},
	});

	// SIMPLE: Navigate to page from URL
	const navigateToPageFromURL = async () => {
		if (!currentReport) return;
		
		const urlParams = new URLSearchParams(window.location.search);
		const pageFromUrl = urlParams.get('page');
		
		if (pageFromUrl) {
			await navigateToSpecificPage(pageFromUrl);
		}
	};

	// Navigate to a specific Power BI page
	const navigateToSpecificPage = async (pageId: string) => {
		if (!currentReport) return;
		
		try {
			const pages = await (currentReport as any).getPages();
			const targetPage = pages.find((page: any) => page.name === pageId);
			if (targetPage) {
				await targetPage.setActive();
			}
		} catch (error) {
			console.error('Power BI navigation error:', error);
		}
	};

	const [eventHandlersMap] = useState<Map<string, (event?: any) => void>>(
		new Map([
			[
				'loaded',
				() => {
					onRenderedPowerBI(false);
					setIsLoading(false);
				},
			],
			[
				'rendered',
				() => {
					setIsLoading(false);
					onRenderedPowerBI(false);
					
					// Navigate to URL page after render
					setTimeout(navigateToPageFromURL, 2000);
				},
			],
			[
				'error',
				(event?: service.ICustomEvent<any>) => {
					if (event) {
						console.error(event.detail);
					}
				},
			],
			[
				'visualClicked', 
				(event?: service.ICustomEvent<any>) => {
					// Visual clicked event handler
				}
			],
			[
				'pageChanged', 
				(event?: service.ICustomEvent<any>) => {
					if (event?.detail?.newPage) {
						const pageId = event.detail.newPage.name;
						
						// Update URL when page changes
						const newUrl = `${window.location.origin}${window.location.pathname}?page=${pageId}`;
						window.history.pushState({}, '', newUrl);
					}
				}
			],
		])
	);

	// Handle browser back/forward
	useEffect(() => {
		const handlePopState = () => {
			setTimeout(navigateToPageFromURL, 500);
		};
		
		// Handle custom Power BI navigation events from favourites
		const handlePowerBINavigate = (event: CustomEvent) => {
			const pageId = event.detail?.pageId;
			if (pageId && currentReport) {
				navigateToSpecificPage(pageId);
			}
		};
		
		window.addEventListener('popstate', handlePopState);
		window.addEventListener('powerbi-navigate', handlePowerBINavigate as (event: Event) => void);
		
		return () => {
			window.removeEventListener('popstate', handlePopState);
			window.removeEventListener('powerbi-navigate', handlePowerBINavigate as (event: Event) => void);
		};
	}, [currentReport]);

	const classNames = useClassNameBuilderFactory()
		.Create()
		.AddConditionalClass(isLoading, styles.embed_container_hide)
		.AddConditionalClass(!isLoading, styles.embed_container_show)
		.GetClassNames();

	useEffect(() => {
		if (embedUrl != null && accessToken != null) {
			setEmbeddedReportConfig({
				...embeddedReportConfig,
				embedUrl: embedUrl,
				accessToken: accessToken,
			});
		}
	}, [embedUrl, accessToken, loading]);

	useEffect(() => {
		if (loading) {
			setIsLoading(loading);
		}
	}, [loading]);

	return (
		<div className={styles.overlay_container}>
			{isLoading && (
				<div className={styles.overlay}>
					<Spinner />
				</div>
			)}
			
			<div style={{ overflow: 'hidden !important', width: '100%', height: '100%' }}>
				<PowerBIEmbed
					embedConfig={embeddedReportConfig}
					eventHandlers={eventHandlersMap}
					cssClassName={reportClass}
					getEmbeddedComponent={(embedObject: Embed) => {
						setCurrentReport(embedObject);
					}}
				/>
			</div>
		</div>
	);
};

export default PowerBIEmbedded;